import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  getBlogPosts, 
  getBlogPostById,
  getRelatedPosts,
  getFeaturedPosts,
  searchBlogPosts
} from '../../services/mockBlogAPI';

// Initial state
const initialState = {
  // Data
  posts: [],
  currentPost: null,
  relatedPosts: [],
  featuredPosts: [],
  searchResults: [],
  
  // Filters
  selectedCategory: null,
  searchQuery: "",
  
  // Pagination
  currentPage: 1,
  totalPages: 0,
  totalPosts: 0,
  hasMore: false,
  postsPerPage: 9,
  
  // UI States
  loading: false,
  searchLoading: false,
  error: null
};

// Async thunk for fetching blog posts
export const fetchBlogPosts = createAsyncThunk(
  'blog/fetchBlogPosts',
  async ({ page = 1, category = null }, { rejectWithValue, getState }) => {
    try {
      const { postsPerPage } = getState().blog;
      const response = await getBlogPosts(page, postsPerPage, category);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching single blog post
export const fetchBlogPostById = createAsyncThunk(
  'blog/fetchBlogPostById',
  async (id, { rejectWithValue }) => {
    try {
      const post = await getBlogPostById(id);
      return post;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching related posts
export const fetchRelatedPosts = createAsyncThunk(
  'blog/fetchRelatedPosts',
  async (postId, { rejectWithValue }) => {
    try {
      const posts = await getRelatedPosts(postId, 3);
      return posts;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching featured posts
export const fetchFeaturedPosts = createAsyncThunk(
  'blog/fetchFeaturedPosts',
  async (_, { rejectWithValue }) => {
    try {
      const posts = await getFeaturedPosts();
      return posts;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for searching blog posts
export const searchBlogs = createAsyncThunk(
  'blog/searchBlogs',
  async (query, { rejectWithValue }) => {
    try {
      const results = await searchBlogPosts(query);
      return results;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for loading more posts
export const loadMorePosts = createAsyncThunk(
  'blog/loadMorePosts',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { currentPage, selectedCategory, postsPerPage } = getState().blog;
      const nextPage = currentPage + 1;
      const response = await getBlogPosts(nextPage, postsPerPage, selectedCategory);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Create blog slice
const blogSlice = createSlice({
  name: 'blog',
  initialState,
  reducers: {
    // Set selected category
    setCategory: (state, action) => {
      state.selectedCategory = action.payload;
      state.currentPage = 1; // Reset to first page when category changes
    },
    
    // Set search query
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    
    // Clear filters
    clearFilters: (state) => {
      state.selectedCategory = null;
      state.searchQuery = "";
      state.searchResults = [];
    },
    
    // Clear current post
    clearCurrentPost: (state) => {
      state.currentPost = null;
      state.relatedPosts = [];
    },
    
    // Clear error
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch blog posts
      .addCase(fetchBlogPosts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBlogPosts.fulfilled, (state, action) => {
        state.loading = false;
        state.posts = action.payload.posts;
        state.currentPage = action.payload.currentPage;
        state.totalPages = action.payload.totalPages;
        state.totalPosts = action.payload.totalPosts;
        state.hasMore = action.payload.hasMore;
      })
      .addCase(fetchBlogPosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch blog post by ID
      .addCase(fetchBlogPostById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBlogPostById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPost = action.payload;
      })
      .addCase(fetchBlogPostById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch related posts
      .addCase(fetchRelatedPosts.pending, (state) => {
        // Don't set loading for related posts (background operation)
      })
      .addCase(fetchRelatedPosts.fulfilled, (state, action) => {
        state.relatedPosts = action.payload;
      })
      .addCase(fetchRelatedPosts.rejected, (state, action) => {
        // Silently fail for related posts
      })
      
      // Fetch featured posts
      .addCase(fetchFeaturedPosts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeaturedPosts.fulfilled, (state, action) => {
        state.loading = false;
        state.featuredPosts = action.payload;
      })
      .addCase(fetchFeaturedPosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Search blogs
      .addCase(searchBlogs.pending, (state) => {
        state.searchLoading = true;
        state.error = null;
      })
      .addCase(searchBlogs.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchBlogs.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.payload;
      })
      
      // Load more posts
      .addCase(loadMorePosts.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadMorePosts.fulfilled, (state, action) => {
        state.loading = false;
        // Append new posts to existing ones
        state.posts = [...state.posts, ...action.payload.posts];
        state.currentPage = action.payload.currentPage;
        state.totalPages = action.payload.totalPages;
        state.hasMore = action.payload.hasMore;
      })
      .addCase(loadMorePosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

// Export actions
export const { 
  setCategory, 
  setSearchQuery, 
  clearFilters, 
  clearCurrentPost, 
  clearError 
} = blogSlice.actions;

// Selectors
export const selectBlogPosts = (state) => state.blog.posts;
export const selectCurrentPost = (state) => state.blog.currentPost;
export const selectRelatedPosts = (state) => state.blog.relatedPosts;
export const selectFeaturedPosts = (state) => state.blog.featuredPosts;
export const selectSearchResults = (state) => state.blog.searchResults;
export const selectSelectedCategory = (state) => state.blog.selectedCategory;
export const selectSearchQuery = (state) => state.blog.searchQuery;
export const selectBlogLoading = (state) => state.blog.loading;
export const selectSearchLoading = (state) => state.blog.searchLoading;
export const selectBlogError = (state) => state.blog.error;
export const selectBlogPagination = (state) => ({
  currentPage: state.blog.currentPage,
  totalPages: state.blog.totalPages,
  totalPosts: state.blog.totalPosts,
  hasMore: state.blog.hasMore
});

// Export reducer
export default blogSlice.reducer;
